import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MinusCircle, TriangleAlert } from 'lucide-react';
import { holdsApi } from '@/services/ewayBillApi';
import { slug } from '@/components/ewayBill/TallyNames';

const ICON = {
  sent: <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />,
  held: <TriangleAlert className="w-4 h-4 shrink-0 text-amber-600" />,
  skipped: <MinusCircle className="w-4 h-4 shrink-0 text-gray-400" />,
};
const note = (r, sent) => (r.result === 'sent' ? `${sent}${r.customer ? ` · ${r.customer}` : ''}` : r.result === 'held' ? (r.reasons || []).join(' · ') : r.note);

const Tile = ({ n, label, cls, testId }) => (
  <div className={`rounded-lg px-3 py-2 text-center ${cls}`}><div className="text-lg font-bold tabular-nums" data-testid={testId}>{n}</div><div className="text-[11px]">{label}</div></div>
);

/** Live result of an auto-push run (bulk invoice upload or Re-check all): sent / need attention / skipped. */
export const AutoPushSummary = ({ runId, onDone }) => {
  const [run, setRun] = useState(null);
  const done = useRef(false);
  useEffect(() => {
    if (!runId) return undefined;
    done.current = false;
    setRun(null);
    let t;
    const poll = () => holdsApi.run(runId).then((r) => {
      setRun(r);
      if (r.status === 'running') t = setTimeout(poll, 1500);
      else if (!done.current) { done.current = true; onDone?.(r); }
    }).catch(() => { t = setTimeout(poll, 3000); });
    poll();
    return () => clearTimeout(t);
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!runId) return null;
  if (!run || run.status === 'running') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-900" data-testid="autopush-running">
        <Loader2 className="w-4 h-4 animate-spin" />Checking the documents and making their e-way bills… {run ? `${run.done} of ${run.total}` : ''}
      </div>
    );
  }
  const sent = run.route === 'portal' ? 'ready for the portal JSON' : run.route === 'gsp' ? 'e-way bill generated' : 'sent to Tally';
  return (
    <div className="space-y-2 rounded-lg border border-teal-200 bg-white p-3" data-testid="autopush-summary">
      <div className="text-sm font-semibold text-[#1A1F2E]">Auto e-way bills{run.status === 'stopped' ? ' — stopped before the end, press Re-check all' : ''}</div>
      <div className="grid grid-cols-3 gap-2">
        <Tile n={run.sent} label={sent} cls="bg-emerald-50 text-emerald-800" testId="autopush-sent" />
        <Tile n={run.held} label="need attention" cls="bg-amber-50 text-amber-800" testId="autopush-held" />
        <Tile n={run.skipped} label="skipped (already have an e-way bill)" cls="bg-gray-50 text-gray-600" testId="autopush-skipped" />
      </div>
      {run.rows.length > 0 && (
        <ul className="max-h-48 overflow-y-auto divide-y rounded-md border border-gray-100 text-xs" data-testid="autopush-rows">
          {run.rows.map((r) => (
            <li key={`${r.doc_type || ''}:${r.doc_no}`} className="flex items-start gap-2 px-2 py-1.5" data-testid={`autopush-row-${slug(r.doc_no)}`}>
              {ICON[r.result]}<span className="font-mono font-semibold whitespace-nowrap">{r.doc_no}</span><span className="text-gray-600">{note(r, sent)}</span>
            </li>
          ))}
        </ul>
      )}
      {run.held > 0 && <p className="text-xs text-gray-500">Held ones are listed under <b>E-Way Bills → Needs attention</b>.</p>}
    </div>
  );
};

export default AutoPushSummary;
